import { graphql } from '@/generated/gql'
import { useLazyQuery } from '@apollo/client'

import { useCallback, useEffect, useState } from 'react'

import { SelectorBasket } from '.'
import {
  useBasketSelectionActions,
  useBasketSelectionState,
} from '../../store/basketSelectionStore'

const query = graphql(`
  query BasketSelection_GetBaskets($filters: GmBasketFiltersInput, $limit: Int, $offset: Int) {
    me {
      gmBaskets(filters: $filters, limit: $limit, offset: $offset) {
        rows {
          id
          friendlyId
          customName
          isCateringTeamOrder
        }
      }
    }
  }
`)

type Props = {
  vendorId?: string
}

export const useAuthenticatedUserBasketSelection = ({ vendorId }: Props) => {
  const [run, { data, refetch, called, loading: queryLoading }] = useLazyQuery(query)
  const { authenticatedBaskets } = useBasketSelectionState()
  const { setAuthenticatedBaskets } = useBasketSelectionActions()
  const [loading, setLoading] = useState(false)

  const onCreate = useCallback(
    (newBasket: SelectorBasket) => {
      setAuthenticatedBaskets((prev) => {
        // map used to prevent any duplicate baskets being created when fetching existing ones
        const authedBaskets = new Map<string, SelectorBasket>()
        authedBaskets.set(newBasket.id, newBasket)

        for (const basket of prev) {
          authedBaskets.set(basket.id, basket)
        }

        return Array.from(authedBaskets.values())
      })
      // Apollo's lazy refetch executes even if the query was never run — without the guard a
      // vendor-suppressed session (team-order contributor) would fire an unfiltered list fetch.
      if (called) refetch()
    },
    [called, refetch, setAuthenticatedBaskets],
  )

  const onDelete = useCallback(
    (basketId: string) => {
      setAuthenticatedBaskets((prev) => {
        const filteredBaskets = prev.filter((basket) => basketId !== basket.id)
        return filteredBaskets
      })
      refetch()
    },
    [refetch, setAuthenticatedBaskets],
  )

  useEffect(() => {
    setAuthenticatedBaskets(data?.me.gmBaskets.rows ?? [])
    setLoading(false)
  }, [data, setAuthenticatedBaskets])

  useEffect(() => {
    if (!vendorId) return
    setLoading(true)
    run({ variables: { offset: 0, limit: 5, filters: { vendorId } } })
  }, [vendorId, run])

  return {
    baskets: authenticatedBaskets,
    loading: loading || queryLoading,
    onCreate,
    onDelete,
    called,
  }
}
